// Keeps the short lived access token fresh with the single use refresh token (SEC-32, SEC-33).
// The server replaces the refresh token on every refresh, so both are saved together. A refresh
// runs once at a time in a tab; a tab that loses a race with another tab picks up the token the
// other tab saved. Sign out ends the refresh token on the server (SEC-34).
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN, REFRESH_TOKEN } from "constants/AuthConstant";

const REFRESH_BEFORE_SECONDS = 120;
const CHECK_EVERY_MS = 60 * 1000;
const OTHER_TAB_WAIT_MS = 1500;

let pending = null;
let timer = null;

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const secondsLeft = (token) => {
  if (!token) return -1;
  try {
    return jwtDecode(token).exp - Date.now() / 1000;
  } catch (e) {
    return -1;
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Saves the tokens from a sign in or refresh answer. */
export const saveTokens = (token, refreshToken) => {
  if (token) localStorage.setItem(AUTH_TOKEN, token);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN, refreshToken);
};

const savedByAnotherTab = (staleToken) => {
  const current = read(AUTH_TOKEN);
  return current && current !== staleToken && secondsLeft(current) > 0 ? current : null;
};

const refresh = async (staleToken) => {
  const refreshToken = read(REFRESH_TOKEN);
  if (!refreshToken) return null;
  try {
    const response = await fetch(`${API_BASE_URL}/Account/refreshtoken`, {
      method: "POST",
      headers: { "Content-type": "application/json; charset=UTF-8" },
      body: JSON.stringify({ token: staleToken, refreshToken }),
    });
    if (response.ok) {
      const data = await response.json();
      if (data && data.token && data.refreshToken) {
        saveTokens(data.token, data.refreshToken);
        return data.token;
      }
    }
  } catch (e) {
    // network error: fall through to the other tab check
  }
  const fromOtherTab = savedByAnotherTab(staleToken);
  if (fromOtherTab) return fromOtherTab;
  await wait(OTHER_TAB_WAIT_MS);
  return savedByAnotherTab(staleToken);
};

/**
 * A usable access token, refreshed first when it is about to expire; null when the session has
 * ended and the user has to sign in again.
 */
export const getValidToken = async () => {
  const token = read(AUTH_TOKEN);
  if (token && secondsLeft(token) > REFRESH_BEFORE_SECONDS) return token;
  if (!pending) {
    pending = refresh(token).finally(() => {
      pending = null;
    });
  }
  const fresh = await pending;
  if (fresh) return fresh;
  return secondsLeft(token) > 0 ? token : null;
};

/** Refreshes in the background so code that reads the saved token directly always finds a valid one. */
export const startTokenRefresh = () => {
  if (timer !== null) return;
  timer = setInterval(() => {
    if (read(AUTH_TOKEN) && read(REFRESH_TOKEN)) getValidToken();
  }, CHECK_EVERY_MS);
};

/** Ends this session's refresh token on the server and forgets it here. */
export const revokeRefreshToken = async () => {
  const refreshToken = read(REFRESH_TOKEN);
  try {
    localStorage.removeItem(REFRESH_TOKEN);
  } catch (e) {
    // storage unavailable: nothing to remove
  }
  if (!refreshToken) return;
  try {
    await fetch(`${API_BASE_URL}/Account/logout`, {
      method: "POST",
      headers: { "Content-type": "application/json; charset=UTF-8" },
      body: JSON.stringify({ refreshToken }),
    });
  } catch (e) {
    // the server ends the token at its expiry anyway
  }
};
